/**
 * NotificationCenter component for PeoplePulse HR Management System.
 * Displays notifications with unread indicators, filtering, and actions.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  BellRing, 
  Check, 
  X, 
  Filter, 
  Search, 
  MoreVertical,
  AlertCircle,
  Info,
  CheckCircle,
  AlertTriangle,
  Calendar,
  User,
  FileText,
  Settings
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from '../hooks/use-toast';
import { apiService } from '../services/apiService';

const NotificationCenter = ({ isOpen, onClose, className = '' }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPriority, setSelectedPriority] = useState('all');
  const [unreadCount, setUnreadCount] = useState(0);
  const [activeTab, setActiveTab] = useState('all');

  // Load notifications
  const loadNotifications = useCallback(async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        offset: '0',
        ...filters
      });

      // Fetch notifications and unread count in parallel
      const [notificationsResponse, unreadResponse] = await Promise.all([
        apiService.get(`/api/notifications?${params}`),
        apiService.get('/api/notifications/unread-count')
      ]);
      
      setNotifications(notificationsResponse);
      setUnreadCount(unreadResponse.unread_count);
    } catch (error) {
      console.error('Error loading notifications:', error);
      toast({
        title: "Error",
        description: "Failed to load notifications",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, []);

  // Load notifications on mount and when filters change
  useEffect(() => {
    if (isOpen) {
      const filters = {};
      if (activeTab === 'unread') filters.unread_only = 'true';
      if (selectedCategory !== 'all') filters.category = selectedCategory;
      if (selectedPriority !== 'all') filters.priority = selectedPriority;
      
      loadNotifications(filters);
    }
  }, [isOpen, activeTab, selectedCategory, selectedPriority, loadNotifications]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      if (isOpen) {
        const filters = {};
        if (activeTab === 'unread') filters.unread_only = 'true';
        if (selectedCategory !== 'all') filters.category = selectedCategory;
        if (selectedPriority !== 'all') filters.priority = selectedPriority;
        loadNotifications(filters);
      }
    };
    
    window.addEventListener('notifications:refresh', handleRefresh);
    return () => {
      window.removeEventListener('notifications:refresh', handleRefresh);
    };
  }, [isOpen, activeTab, selectedCategory, selectedPriority, loadNotifications]);

  // Requirement 6.5: Poll for notifications every 30 seconds as a fallback
  // This ensures users receive updates even if WebSocket connection fails
  useEffect(() => {
    // Only poll when the notification center is open
    if (!isOpen) return;

    const POLLING_INTERVAL = 30000; // 30 seconds
    let pollFailureCount = 0;
    const MAX_FAILURES = 3;

    const pollNotifications = async () => {
      try {
        // Silently fetch unread count without showing loading state
        const unreadResponse = await apiService.getUnreadNotificationCount();
        
        // Only update if count changed to avoid unnecessary re-renders
        setUnreadCount(prevCount => {
          if (prevCount !== unreadResponse.unread_count) {
            // If unread count increased, refresh the full notification list
            if (unreadResponse.unread_count > prevCount) {
              const filters = {};
              if (activeTab === 'unread') filters.unread_only = 'true';
              if (selectedCategory !== 'all') filters.category = selectedCategory;
              if (selectedPriority !== 'all') filters.priority = selectedPriority;
              loadNotifications(filters);
            }
            return unreadResponse.unread_count;
          }
          return prevCount;
        });
        
        // Reset failure count on success
        pollFailureCount = 0;
      } catch (error) {
        pollFailureCount++;
        console.error(`Notification polling failed (attempt ${pollFailureCount}/${MAX_FAILURES}):`, error);
        
        // Only show error toast after multiple consecutive failures
        if (pollFailureCount >= MAX_FAILURES) {
          toast({
            title: "Connection Issue",
            description: "Unable to check for new notifications. Will retry automatically.",
            variant: "destructive"
          });
          // Reset counter to avoid spamming toasts
          pollFailureCount = 0;
        }
      }
    };

    // Start polling interval
    const intervalId = setInterval(pollNotifications, POLLING_INTERVAL);

    // Cleanup on unmount or when isOpen changes
    return () => {
      clearInterval(intervalId);
    };
  }, [isOpen, activeTab, selectedCategory, selectedPriority, loadNotifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      // Find the notification to check if it was already read
      const notification = notifications.find(n => n.id === notificationId);
      const wasUnread = notification && !notification.read;
      
      // Requirement 6.2: Call correct endpoint for marking notification as read
      await apiService.markNotificationRead(notificationId);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      // Only decrement if the notification was actually unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      toast({
        title: "Success",
        description: "Notification marked as read"
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive"
      });
    }
  };

  // Dismiss notification
  const dismissNotification = async (notificationId) => {
    try {
      // Use apiService method for dismissing notification
      await apiService.dismissNotification(notificationId);
      
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      
      toast({
        title: "Success",
        description: "Notification dismissed"
      });
    } catch (error) {
      console.error('Error dismissing notification:', error);
      toast({
        title: "Error",
        description: "Failed to dismiss notification",
        variant: "destructive"
      });
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      // Requirement 6.3: Call correct endpoint for marking all notifications as read
      await apiService.markAllNotificationsRead(
        selectedCategory !== 'all' ? selectedCategory : null
      );
      
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true, read_at: new Date().toISOString() }))
      );
      
      setUnreadCount(0);
      
      toast({
        title: "Success",
        description: "All notifications marked as read"
      });
    } catch (error) {
      console.error('Error marking all as read:', error);
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive"
      });
    }
  };

  // Handle notification action
  const handleNotificationAction = async (action, notificationId) => {
    try {
      if (action.endpoint) {
        if (action.action_type === 'view') {
          // Check if it's an external URL or internal route
          if (action.endpoint.startsWith('http://') || action.endpoint.startsWith('https://')) {
            // External URL - open in new tab
            window.open(action.endpoint, '_blank');
          } else {
            // Internal route - use SPA navigation (would need router context)
            // For now, use location.href but this should be replaced with router navigation
            window.location.href = action.endpoint;
          }
        } else {
          // Make API call for other actions
          const method = action.method || 'POST';
          const payload = action.payload || {};
          
          await apiService.request(method, action.endpoint, payload);
          
          toast({
            title: "Success",
            description: `Action "${action.label}" completed successfully`
          });
          
          // Refresh notifications
          loadNotifications();
        }
      }
    } catch (error) {
      console.error('Error handling notification action:', error);
      toast({
        title: "Error",
        description: `Failed to execute action: ${action.label}`,
        variant: "destructive"
      });
    }
  };

  // Get icon for notification category
  const getCategoryIcon = (category) => {
    const icons = {
      absence_request: Calendar,
      absence_approval: CheckCircle,
      observation: User,
      system_alert: AlertCircle,
      report: FileText,
      ai_conversation: Info,
      general: Bell
    };
    
    const IconComponent = icons[category] || Bell;
    return <IconComponent className="w-4 h-4" />;
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-100 text-gray-800',
      normal: 'bg-blue-100 text-blue-800',
      high: 'bg-orange-100 text-orange-800',
      urgent: 'bg-red-100 text-red-800'
    };
    return colors[priority] || colors.normal;
  };

  // Filter notifications based on search term
  const filteredNotifications = notifications.filter(notification => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      notification.title.toLowerCase().includes(searchLower) ||
      notification.message.toLowerCase().includes(searchLower)
    );
  });

  if (!isOpen) return null;

  return (
    <div className={`fixed inset-0 z-50 ${className}`}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      
      {/* Notification Panel */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white shadow-xl">
        <Card className="h-full rounded-none border-0">
          <CardHeader className="border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <BellRing className="w-5 h-5" />
                Notifications
                {unreadCount > 0 && (
                  <Badge variant="destructive" className="ml-2">
                    {unreadCount}
                  </Badge>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={markAllAsRead}
                    className="text-xs"
                  >
                    Mark all read
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={onClose}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Search and Filters */}
            <div className="space-y-3 pt-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search notifications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              
              <div className="flex gap-2">
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    <SelectItem value="absence_request">Absence Requests</SelectItem>
                    <SelectItem value="absence_approval">Absence Approvals</SelectItem>
                    <SelectItem value="observation">Observations</SelectItem>
                    <SelectItem value="system_alert">System Alerts</SelectItem>
                    <SelectItem value="report">Reports</SelectItem>
                    <SelectItem value="ai_conversation">AI Conversations</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Priorities</SelectItem>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0 flex-1">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full">
              <TabsList className="grid w-full grid-cols-2 rounded-none border-b">
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="unread" className="relative">
                  Unread
                  {unreadCount > 0 && (
                    <Badge variant="destructive" className="ml-1 h-4 w-4 p-0 text-xs">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="all" className="h-full mt-0">
                <NotificationList
                  notifications={filteredNotifications}
                  loading={loading}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismissNotification}
                  onAction={handleNotificationAction}
                  getCategoryIcon={getCategoryIcon}
                  getPriorityColor={getPriorityColor}
                />
              </TabsContent>
              
              <TabsContent value="unread" className="h-full mt-0">
                <NotificationList
                  notifications={filteredNotifications.filter(n => !n.read)}
                  loading={loading}
                  onMarkAsRead={markAsRead}
                  onDismiss={dismissNotification}
                  onAction={handleNotificationAction}
                  getCategoryIcon={getCategoryIcon}
                  getPriorityColor={getPriorityColor}
                />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Notification List Component
const NotificationList = ({ 
  notifications, 
  loading, 
  onMarkAsRead, 
  onDismiss, 
  onAction,
  getCategoryIcon,
  getPriorityColor 
}) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (notifications.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-32 text-gray-500">
        <Bell className="w-8 h-8 mb-2" />
        <p>No notifications found</p>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full">
      <div className="divide-y">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onMarkAsRead={onMarkAsRead}
            onDismiss={onDismiss}
            onAction={onAction}
            getCategoryIcon={getCategoryIcon}
            getPriorityColor={getPriorityColor}
          />
        ))}
      </div>
    </ScrollArea>
  );
};

// Individual Notification Item Component
const NotificationItem = ({ 
  notification, 
  onMarkAsRead, 
  onDismiss, 
  onAction,
  getCategoryIcon,
  getPriorityColor 
}) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);
    
    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  return (
    <div className={`p-4 hover:bg-gray-50 transition-colors ${!notification.read ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''}`}>
      <div className="flex items-start gap-3">
        {/* Category Icon */}
        <div className="flex-shrink-0 mt-1">
          {getCategoryIcon(notification.category)}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">
              <h4 className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-700'}`}>
                {notification.title}
              </h4>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {notification.message}
              </p>
            </div>
            
            {/* Actions Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {!notification.read && (
                  <DropdownMenuItem onClick={() => onMarkAsRead(notification.id)}>
                    <Check className="w-4 h-4 mr-2" />
                    Mark as read
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem 
                  onClick={() => onDismiss(notification.id)}
                  className="text-red-600"
                >
                  <X className="w-4 h-4 mr-2" />
                  Dismiss
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          {/* Metadata */}
          <div className="flex items-center gap-2 mt-2">
            <Badge className={`text-xs ${getPriorityColor(notification.priority)}`}>
              {notification.priority}
            </Badge>
            <span className="text-xs text-gray-500">
              {formatDate(notification.created_at)}
            </span>
            {!notification.read && (
              <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            )}
          </div>
          
          {/* Action Buttons */}
          {notification.actions && notification.actions.length > 0 && (
            <div className="flex gap-2 mt-3">
              {notification.actions.map((action) => (
                <Button
                  key={action.id}
                  size="sm"
                  variant={action.style === 'primary' ? 'default' : 'outline'}
                  className={`text-xs ${
                    action.style === 'success' ? 'bg-green-600 hover:bg-green-700 text-white' :
                    action.style === 'danger' ? 'bg-red-600 hover:bg-red-700 text-white' :
                    action.style === 'warning' ? 'bg-orange-600 hover:bg-orange-700 text-white' : ''
                  }`}
                  onClick={() => onAction(action, notification.id)}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;